import * as React from 'react';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import siteLogo from '../assets/images/sitelogo2.png';
import './NavBar.css';

const STATIC_NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Blog', to: '/blog' },
  { label: 'Projects', to: '/projects' },
];

const listVariants = {
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
  hidden: {},
};

const itemVariants = {
  hidden: { opacity: 0, y: -24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
  exit: { opacity: 0, y: -24, transition: { duration: 0.2 } },
};

function NavBar() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const isAdmin = currentUser?.role === 'admin';

  const handleSignOut = async () => {
    await signOut(auth);
    setMenuOpen(false);
    window.scrollTo(0, 0);
    navigate('/');
  };

  const [menuOpen, setMenuOpen] = React.useState(false);

  const pastHero = useScrollTrigger({
    disableHysteresis: true,
    threshold: window.innerHeight * 0.5,
  });

  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen);
    return () => document.body.classList.remove('menu-open');
  }, [menuOpen]);

  const navbarVisible = !isHomePage || pastHero || menuOpen;

  return (
    <>
      {/* Full-screen overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="nav-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.nav
              variants={listVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(0.75rem, 3vh, 2.5rem)' }}
            >
              {currentUser && (
                <motion.div variants={itemVariants} style={{ textAlign: 'center' }}>
                  <Typography sx={{ color: 'white', fontSize: 'clamp(0.75rem, 2.5vh, 1.1rem)', opacity: 0.6 }}>
                    Hello, {currentUser.name}!
                  </Typography>
                </motion.div>
              )}

              {/* Static links */}
              {STATIC_NAV_LINKS.map(({ label, to }) => (
                <motion.div key={to} variants={itemVariants}>
                  <Link to={to} className="nav-overlay-link" onClick={() => setMenuOpen(false)}>
                    {label}
                  </Link>
                </motion.div>
              ))}

              {/* Account link — only when signed in */}
              {currentUser && (
                <motion.div variants={itemVariants}>
                  <Link to="/account" className="nav-overlay-link" onClick={() => setMenuOpen(false)}>
                    My Account
                  </Link>
                </motion.div>
              )}

              {/* Admin link — only for admins */}
              {isAdmin && (
                <motion.div variants={itemVariants}>
                  <Link to="/admin" className="nav-overlay-link" onClick={() => setMenuOpen(false)}>
                    Admin
                  </Link>
                </motion.div>
              )}

              {/* Sign In / Sign Out */}
              <motion.div variants={itemVariants}>
                {currentUser ? (
                  <button className="nav-overlay-link nav-overlay-btn" onClick={handleSignOut}>
                    Sign Out
                  </button>
                ) : (
                  <Link to="/SignIn" className="nav-overlay-link" onClick={() => setMenuOpen(false)}>
                    Sign In
                  </Link>
                )}
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navbar bar */}
      <AnimatePresence>
        {navbarVisible && (
          <motion.nav
            className="navbar"
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <Grid container sx={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
              <Grid size={6}>
                {!menuOpen && (
                  <Link to="/">
                    <img
                      src={siteLogo}
                      alt="Grahame Ung"
                      style={{ maxHeight: '40px', maxWidth: '100%', width: 'auto', height: 'auto', display: 'block' }}
                    />
                  </Link>
                )}
              </Grid>

              <Grid size={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                  {!menuOpen && currentUser && (
                    <Typography sx={{ color: 'white', px: 1 }}>
                      Hello, {currentUser.name}!
                    </Typography>
                  )}
                  <IconButton onClick={() => setMenuOpen(o => !o)} sx={{ color: 'white' }}>
                    {menuOpen ? <CloseIcon /> : <MenuIcon />}
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}

export default NavBar;
